import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency, CURRENCIES, CurrencyCode } from '../../../context/CurrencyContext';
import { useTenant } from '../../../context/TenantContext';

export default function Header({ sidebarCollapsed }: { sidebarCollapsed?: boolean }) {
  const [showNotifs, setShowNotifs] = useState(false);
  const [showCurrency, setShowCurrency] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { currency, setCurrency } = useCurrency();
  const { currentUser, agencyProfile, signOut } = useTenant();
  const navigate = useNavigate();
  const notifRef = useRef<HTMLDivElement>(null);
  const currencyRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  const brand = agencyProfile.brandColor || '#0D9488';

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifs(false);
      if (currencyRef.current && !currencyRef.current.contains(e.target as Node)) setShowCurrency(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setShowUserMenu(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // ... existing code ...

  return (
    <header className={`fixed top-0 right-0 h-16 bg-white border-b border-gray-100 flex items-center justify-between px-5 z-30 transition-all duration-300 ${sidebarCollapsed ? 'left-16' : 'left-64'}`}>
      {/* Left: breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400">
        <i className="ri-home-4-line text-base" />
        <span>/</span>
        <span className="text-gray-600 font-medium">Dashboard</span>
      </div>

      {/* Center: search */}
      <div className="flex-1 max-w-md mx-8">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input type="text" placeholder="Search leads, bookings, customers..."
            className="w-full pl-9 pr-12 py-2 bg-gray-50 rounded-full text-sm text-gray-600 placeholder-gray-400 border border-gray-100 focus:outline-none focus:border-gray-300 focus:bg-white transition-colors" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-300 bg-gray-100 px-1.5 py-0.5 rounded font-mono">⌘K</span>
        </div>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-2">
        {/* WhatsApp status */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 rounded-full">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-xs text-green-600 font-medium">WhatsApp</span>
        </div>

        {/* Currency Selector */}
        <div className="relative" ref={currencyRef}>
          <button onClick={() => setShowCurrency((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
            <span className="text-sm">{currency.flag}</span>
            <span className="text-xs font-semibold text-gray-700">{currency.code}</span>
            <i className="ri-arrow-down-s-line text-gray-400 text-xs" />
          </button>
          {showCurrency && (
            <div className="absolute right-0 top-12 w-48 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-4 pt-3 pb-2">Display Currency</p>
              {CURRENCIES.map((c) => (
                <button key={c.code} onClick={() => { setCurrency(c.code as CurrencyCode); setShowCurrency(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer text-left ${currency.code === c.code ? 'bg-gray-50' : ''}`}>
                  <span className="text-base">{c.flag}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{c.code}</p>
                    <p className="text-xs text-gray-400 truncate">{c.name}</p>
                  </div>
                  <span className="text-xs font-bold text-gray-500 flex-shrink-0">{c.symbol}</span>
                  {currency.code === c.code && <i className="ri-check-line text-sm flex-shrink-0" style={{ color: brand }} />}
                </button>
              ))}
              <div className="px-4 py-2.5 border-t border-gray-50">
                <p className="text-xs text-gray-400 leading-relaxed">Rates are indicative. All stored amounts are in AED.</p>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button onClick={() => setShowNotifs((v) => !v)}
            className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-500 cursor-pointer transition-colors">
            <i className="ri-notification-3-line text-lg" />
          </button>
          {showNotifs && (
            <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                <p className="font-semibold text-gray-800 text-sm">Notifications</p>
              </div>
              <div className="px-4 py-10 flex flex-col items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100">
                  <i className="ri-notification-3-line text-gray-400 text-lg" />
                </div>
                <p className="text-sm text-gray-500 text-center">No notifications yet</p>
                <p className="text-xs text-gray-400 text-center">Activity will appear here as you use the system</p>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative" ref={userRef}>
          <button onClick={() => setShowUserMenu((v) => !v)}
            className="flex items-center gap-2 pl-1 cursor-pointer group">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-semibold"
              style={{ backgroundColor: brand }}>
              {currentUser.avatar || 'U'}
            </div>
            <i className="ri-arrow-down-s-line text-gray-400 text-sm group-hover:text-gray-600 transition-colors" />
          </button>
          {showUserMenu && (
            <div className="absolute right-0 top-12 w-56 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50">
              <div className="px-4 py-3 border-b border-gray-50">
                <p className="text-sm font-semibold text-gray-800 truncate">{currentUser.name}</p>
                <p className="text-xs text-gray-400 truncate">{currentUser.email}</p>
                <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: `${brand}15`, color: brand }}>
                  {currentUser.role.replace('_', ' ')}
                </span>
              </div>
              <div className="py-1">
                <button onClick={() => { navigate('/settings'); setShowUserMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer text-left">
                  <i className="ri-settings-3-line text-gray-400 text-sm" />
                  <span className="text-sm text-gray-700">Settings</span>
                </button>
                <button onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 transition-colors cursor-pointer text-left">
                  <i className="ri-logout-box-line text-red-400 text-sm" />
                  <span className="text-sm text-red-500 font-medium">Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
